import { toTimestamp } from '../utils/addWebSocketToReplication'
import { toISOString, toISOStringOrNull } from '../utils/pushSafetyUtils'
import { createReplication } from './createReplication'
import type { UserPreferencesCollection } from '../index'

/**
 * UserPreferences sync. The backend pull endpoint already filters to the
 * authenticated user, but the WebSocket broadcast channel is workspace-wide,
 * so we additionally filter incoming WS docs by `userId` to ensure no other
 * user's preferences are ever applied to local RxDB.
 */
export const setupUserPreferencesReplication = (
  collection: UserPreferencesCollection,
  envId: string,
  currentUserId: string,
) =>
  createReplication({
    entityName: 'userpreferences',
    displayName: 'UserPreferences',
    collection,
    envId,
    apiEntityName: 'user-preferences',
    documentFilter: (doc) => doc?.userId === currentUserId,
    mapFromServer: (doc) => ({
      id: doc.id,
      envId: doc.envId,
      userId: doc.userId,
      language: doc.language ?? 'en-US',
      dateFormat: doc.dateFormat ?? 'MM/dd/yyyy',
      timeFormat: doc.timeFormat ?? '12h',
      weekStartsOn: typeof doc.weekStartsOn === 'number' ? doc.weekStartsOn : 0,
      theme: doc.theme ?? 'system',
      defaultView: doc.defaultView ?? 'today',
      workingHoursJson: doc.workingHoursJson ?? null,
      notificationsJson: doc.notificationsJson ?? null,
      timeZone: doc.timeZone ?? 'UTC',
      isDeleted: doc.isDeleted || false,
      createdOn: toTimestamp(doc.createdOn) ?? new Date(doc.createdOn).getTime(),
      createdBy: doc.createdBy,
      updatedOn: toTimestamp(doc.updatedOn) ?? new Date(doc.updatedOn).getTime(),
      updatedBy: doc.updatedBy,
      deletedOn: doc.deletedOn
        ? (toTimestamp(doc.deletedOn) ?? new Date(doc.deletedOn).getTime())
        : null,
      deletedBy: doc.deletedBy ?? null,
    }),
    mapToServer: (state, envId) => ({
      id: state.id,
      envId,
      userId: state.userId,
      language: state.language,
      dateFormat: state.dateFormat,
      timeFormat: state.timeFormat,
      weekStartsOn: state.weekStartsOn,
      theme: state.theme,
      defaultView: state.defaultView,
      workingHoursJson: state.workingHoursJson ?? null,
      notificationsJson: state.notificationsJson ?? null,
      timeZone: state.timeZone,
      isDeleted: state.isDeleted,
      createdOn: toISOString(state.createdOn),
      createdBy: state.createdBy,
      updatedOn: toISOString(state.updatedOn),
      updatedBy: state.updatedBy,
      deletedOn: toISOStringOrNull(state.deletedOn),
      deletedBy: state.deletedBy,
    }),
  })
